/**
 * useDesignerDraftAutosave — persist the in-progress cycle design into
 * cycle.metadata.designerDraft (Supabase) for CycleDesignerWizard (desktop
 * and phone).
 *
 * Assignments used to live only in React state — the debounced save was often
 * cancelled on re-render (cleanup cleared the timer) and marked "saved" before
 * the DB write succeeded, so switching paths / remounting lost assignees. The
 * shape below (flush-on-cleanup, immediate save on assignment changes, unmount
 * flush) is the fix; keep all three parts together.
 *
 * Launch payload (owner ruling #8, Option A, 2026-09-22): when the caller
 * passes `launch`, the same write also keeps cycle.metadata.launchPayload — the
 * exact `launch_cycle_design` payload, present only while the design passes the
 * Designer's pre-launch checks (null otherwise) — so iOS can launch the cycle.
 * The launch transaction clears it (see `buildCycleMetadataPatch`).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useCatchballLoopStore } from '@/stores/prioritization/catchballLoopStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import type { Track } from '@/types/prioritization/track';
import type { CatchballLoop } from '@/types/prioritization/catchballLoop';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import { isCycleDesignerComplete } from '../lib/cycleDesignerPhase';
import { DESIGNER_DRAFT_METADATA_KEY, draftSnapshot } from '../lib/designerDraft';
import {
  LAUNCH_PAYLOAD_METADATA_KEY,
  buildStoredLaunchPayload,
  launchPayloadKey,
  type StoredLaunchPayload,
} from '../lib/launchCycleDesigner';
import type { CycleDesignerFormState, DesignerPath, DesignerStep, OrgPathNode, PathMode } from '../types';

const EMPTY_TRACKS: Track[] = [];
const EMPTY_LOOPS: CatchballLoop[] = [];

/** What the launch payload needs beyond the draft — the same inputs the Launch button passes. */
export interface DraftAutosaveLaunchInputs {
  orgNodes: OrgPathNode[];
  orgById: Map<string, OrgPathNode>;
  pathMode: PathMode;
  ownerRef?: CatchballParticipantRef;
}

interface DraftAutosaveOptions {
  cycleId: string | undefined;
  canConfigure: boolean;
  /** While launching, the draft is being stripped — never write over that. */
  launching: boolean;
  form: CycleDesignerFormState;
  paths: DesignerPath[];
  step: DesignerStep;
  /** Omit to leave cycle.metadata.launchPayload untouched. */
  launch?: DraftAutosaveLaunchInputs;
}

function storedLaunchPayloadOf(metadata: unknown): StoredLaunchPayload | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const raw = (metadata as Record<string, unknown>)[LAUNCH_PAYLOAD_METADATA_KEY];
  return raw && typeof raw === 'object' ? (raw as StoredLaunchPayload) : null;
}

export function useDesignerDraftAutosave({
  cycleId,
  canConfigure,
  launching,
  form,
  paths,
  step,
  launch,
}: DraftAutosaveOptions): void {
  const updateCycle = useDegapProcessStore((s) => s.updateCycle);
  const cycle = useDegapProcessStore((s) =>
    cycleId ? s.cycles.find((c) => c.id === cycleId) : undefined,
  );
  const cycleTracks = useTrackStore(
    useShallow((s) => (cycleId ? s.tracks.filter((t) => t.cycleId === cycleId) : EMPTY_TRACKS)),
  );
  const cycleLoops = useCatchballLoopStore(
    useShallow((s) => (cycleId ? s.loops.filter((l) => l.cycleId === cycleId) : EMPTY_LOOPS)),
  );
  const originatorId = cycle?.orgScopeNodeId ?? null;
  const originatorOrgUnitName = useEnterpriseIdentityStore((s) =>
    originatorId ? s.orgUnits.get(originatorId)?.displayName ?? null : null,
  );

  // The payload as the Launch button would submit it right now (undefined when
  // the caller did not opt in, null when the design is not launchable).
  const launchOrgNodes = launch?.orgNodes;
  const launchOrgById = launch?.orgById;
  const launchPathMode = launch?.pathMode;
  const ownerType = launch?.ownerRef?.type;
  const ownerId = launch?.ownerRef?.id;
  const ownerName = launch?.ownerRef?.displayName;
  const computeLaunchPayload = useCallback((): StoredLaunchPayload | null | undefined => {
    if (!launchOrgNodes || !launchOrgById || !launchPathMode || !cycle) return undefined;
    return buildStoredLaunchPayload({
      cycle,
      form,
      paths,
      orgNodes: launchOrgNodes,
      orgById: launchOrgById,
      pathMode: launchPathMode,
      existingTracks: [...cycleTracks].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
      existingLoops: cycleLoops,
      ownerRef: ownerType && ownerId ? { type: ownerType, id: ownerId, displayName: ownerName ?? '' } : undefined,
      originatorOrgUnitName,
    });
  }, [
    launchOrgNodes, launchOrgById, launchPathMode, cycle, form, paths, cycleTracks, cycleLoops,
    ownerType, ownerId, ownerName, originatorOrgUnitName,
  ]);
  const launchKey = useMemo(() => {
    const payload = computeLaunchPayload();
    return payload === undefined ? null : launchPayloadKey(payload);
  }, [computeLaunchPayload]);

  const lastSavedDraftRef = useRef<string | null>(null);
  const lastSavedLaunchKeyRef = useRef<string | null>(null);
  const draftTimerRef = useRef<number | null>(null);
  const formRef = useRef(form);
  const pathsRef = useRef(paths);
  const stepRef = useRef(step);
  const computeLaunchPayloadRef = useRef(computeLaunchPayload);
  formRef.current = form;
  pathsRef.current = paths;
  stepRef.current = step;
  computeLaunchPayloadRef.current = computeLaunchPayload;

  const persistDraftNow = useCallback(async (): Promise<boolean> => {
    if (!cycleId || !canConfigure || launching) return false;
    const latest = useDegapProcessStore.getState().cycles.find((c) => c.id === cycleId);
    if (!latest || isCycleDesignerComplete(latest)) return false;
    const snapshot = draftSnapshot(formRef.current, pathsRef.current, stepRef.current);
    const payload = computeLaunchPayloadRef.current();
    const payloadKey = payload === undefined ? null : launchPayloadKey(payload);
    const draftChanged = lastSavedDraftRef.current === null || snapshot !== lastSavedDraftRef.current;
    const launchChanged = payloadKey !== null && payloadKey !== lastSavedLaunchKeyRef.current;
    if (!draftChanged && !launchChanged) return true;
    const baseMeta =
      latest.metadata && typeof latest.metadata === 'object'
        ? (latest.metadata as Record<string, unknown>)
        : {};
    const now = new Date().toISOString();
    const result = await updateCycle(cycleId, {
      metadata: {
        ...baseMeta,
        ...(draftChanged
          ? {
              [DESIGNER_DRAFT_METADATA_KEY]: {
                ...(JSON.parse(snapshot) as Record<string, unknown>),
                savedAt: now,
              },
            }
          : {}),
        ...(launchChanged ? { [LAUNCH_PAYLOAD_METADATA_KEY]: payload ? { ...payload, savedAt: now } : null } : {}),
      },
    });
    if (result.success) {
      if (draftChanged) lastSavedDraftRef.current = snapshot;
      if (launchChanged) lastSavedLaunchKeyRef.current = payloadKey;
      return true;
    }
    return false;
  }, [cycleId, canConfigure, launching, updateCycle]);

  useEffect(() => {
    if (!cycleId || !cycle || !canConfigure) return;
    if (isCycleDesignerComplete(cycle)) return;
    const snapshot = draftSnapshot(form, paths, step);
    // First run records the hydrated state — only actual edits trigger a draft
    // save. The launch payload is compared with what the cycle already holds,
    // so a complete design saved before payloads existed gets one on open.
    if (lastSavedDraftRef.current === null) {
      lastSavedDraftRef.current = snapshot;
      lastSavedLaunchKeyRef.current = launchPayloadKey(storedLaunchPayloadOf(cycle.metadata));
    }
    const launchChanged = launchKey !== null && launchKey !== lastSavedLaunchKeyRef.current;
    if (snapshot === lastSavedDraftRef.current && !launchChanged) return;
    if (draftTimerRef.current !== null) window.clearTimeout(draftTimerRef.current);
    draftTimerRef.current = window.setTimeout(() => {
      draftTimerRef.current = null;
      void persistDraftNow();
    }, 400);
    return () => {
      // Flush instead of discarding — otherwise a cycle-store update mid-debounce
      // cancels the write and path assignees never reach the DB.
      if (draftTimerRef.current !== null) {
        window.clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
        void persistDraftNow();
      }
    };
  }, [cycleId, cycle, canConfigure, form, paths, step, launchKey, persistDraftNow]);

  // Node assignees must hit the DB immediately — not wait on debounce.
  const assignmentsFingerprint = useMemo(
    () => JSON.stringify(paths.map((p) => [p.id, p.nodeAssignments ?? null])),
    [paths],
  );
  const assignmentsReadyRef = useRef(false);
  useEffect(() => {
    if (!assignmentsReadyRef.current) {
      assignmentsReadyRef.current = true;
      return;
    }
    if (draftTimerRef.current !== null) {
      window.clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    void persistDraftNow();
  }, [assignmentsFingerprint, persistDraftNow]);

  // Unmount flush: catch the last edit when leaving immediately after a change.
  const flushDraftRef = useRef<() => void>(() => {});
  flushDraftRef.current = () => {
    void persistDraftNow();
  };
  useEffect(() => () => flushDraftRef.current(), []);
}
